/**
 * Product Card JavaScript
 * Handles quick-add functionality with Ajax Cart API
 */

(function () {
  "use strict";

  class ProductCard {
    constructor(element) {
      this.element = element;
      this.quickAddBtn = element.querySelector(".product-card__quick-add");
      this.dropdown = element.querySelector(".product-card__quick-add-dropdown");

      if (this.quickAddBtn) {
        this.init();
      }
    }

    init() {
      // Handle quick add button click
      this.quickAddBtn.addEventListener("click", this.handleQuickAddClick.bind(this));

      // Handle variant selection if dropdown exists
      if (this.dropdown) {
        const variantButtons = this.dropdown.querySelectorAll(".product-card__variant-option");
        variantButtons.forEach((btn) => {
          btn.addEventListener("click", this.handleVariantClick.bind(this));
        });

        // Close dropdown when clicking outside
        document.addEventListener("click", this.handleClickOutside.bind(this));

        // Reposition dropdown on window resize or scroll
        window.addEventListener("resize", () => {
          if (!this.dropdown.hasAttribute("hidden")) {
            this.positionDropdown();
          }
        });
        window.addEventListener("scroll", () => {
          if (!this.dropdown.hasAttribute("hidden")) {
            this.positionDropdown();
          }
        });
      }
    }

    positionDropdown() {
      if (!this.dropdown || !this.element) return;

      const rect = this.element.getBoundingClientRect();
      const infoSection = this.element.querySelector(".product-card__info");
      const infoRect = infoSection ? infoSection.getBoundingClientRect() : rect;

      // Position dropdown below the product card info section
      this.dropdown.style.left = `${rect.left}px`;
      this.dropdown.style.top = `${infoRect.bottom}px`;
      this.dropdown.style.width = `${rect.width}px`;
    }

    handleQuickAddClick(e) {
      e.preventDefault();
      e.stopPropagation();

      // If has variants, toggle dropdown
      if (this.dropdown) {
        const isHidden = this.dropdown.hasAttribute("hidden");
        if (isHidden) {
          this.positionDropdown(); // Position before showing
          this.dropdown.removeAttribute("hidden");
          this.element.classList.add("product-card--dropdown-active");
        } else {
          this.dropdown.setAttribute("hidden", "");
          this.element.classList.remove("product-card--dropdown-active");
        }
      } else {
        // No variants, add default variant directly
        const productHandle = this.quickAddBtn.dataset.productHandle;
        this.addToCart(productHandle, null);
      }
    }

    handleVariantClick(e) {
      const variantId = e.currentTarget.dataset.variantId;
      if (!variantId || e.currentTarget.disabled) return;

      this.addToCart(null, variantId);

      // Close dropdown
      if (this.dropdown) {
        this.dropdown.setAttribute("hidden", "");
        this.element.classList.remove("product-card--dropdown-active");
      }
    }

    handleClickOutside(e) {
      if (!this.element.contains(e.target) && this.dropdown) {
        this.dropdown.setAttribute("hidden", "");
        this.element.classList.remove("product-card--dropdown-active");
      }
    }

    async addToCart(productHandle, variantId) {
      try {
        // Show loading state
        this.quickAddBtn.disabled = true;

        // If we have variantId, use it; otherwise fetch the default variant
        let id = variantId;
        if (!id && productHandle) {
          // Fetch product data to get default variant
          const response = await fetch(`/products/${productHandle}.js`);
          const product = await response.json();
          id = product.variants[0].id;
        }

        // Add to cart via Ajax Cart API
        const formData = {
          items: [
            {
              id: id,
              quantity: 1,
            },
          ],
        };

        const addResponse = await fetch(window.theme.routes.cart_add_url + ".js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });

        if (!addResponse.ok) {
          throw new Error("Failed to add item to cart");
        }

        const result = await addResponse.json();

        // Update cart count
        this.updateCartCount();

        // Show success feedback
        this.showSuccessMessage();

        // Dispatch cart update event
        document.dispatchEvent(
          new CustomEvent("cart:item-added", {
            detail: {
              variant_id: id,
              product_title: result.items[0].product_title,
              price: result.items[0].price,
            },
          }),
        );
      } catch (error) {
        console.error("Error adding to cart:", error);
        this.showErrorMessage("Unable to add item to cart. Please try again.");
      } finally {
        this.quickAddBtn.disabled = false;
      }
    }

    async updateCartCount() {
      // Use centralized cart count updater from theme.js
      if (window.theme && window.theme.updateCartCount) {
        const count = await window.theme.updateCartCount();

        // Dispatch cart updated event for other listeners
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { item_count: count },
          }),
        );
      } else {
        console.warn("theme.updateCartCount not available");
      }
    }

    showSuccessMessage() {
      // Create temporary success indicator
      const originalIcon = this.quickAddBtn.innerHTML;
      this.quickAddBtn.innerHTML = `
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 6l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      setTimeout(() => {
        this.quickAddBtn.innerHTML = originalIcon;
      }, 2000);
    }

    showErrorMessage(message) {
      alert(message); // Simple alert for now, can be enhanced with toast notification
    }
  }

  // Initialize all product cards
  function initProductCards() {
    const productCards = document.querySelectorAll(".product-card");
    productCards.forEach((card) => {
      new ProductCard(card);
    });
  }

  // Run on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initProductCards);
  } else {
    initProductCards();
  }

  // Reinitialize when new products are loaded dynamically
  document.addEventListener("product-cards:loaded", initProductCards);
})();
